import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import {
  DollarSign, TrendingUp, TrendingDown, Building2, Users, Briefcase,
  PieChart, BarChart3, ArrowUpRight, ArrowDownRight, Plus, RefreshCw,
  Wallet, Target, Clock, AlertCircle
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart as RechartsPie, Pie, Cell, Legend } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

const FinancialDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  const [dashboardData, setDashboardData] = useState(null);
  const [platformRevenue, setPlatformRevenue] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [startups, setStartups] = useState([]);
  
  // Dialog states
  const [addFinancialsOpen, setAddFinancialsOpen] = useState(false);
  const [addInvestmentOpen, setAddInvestmentOpen] = useState(false);
  
  // Forms
  const [financialForm, setFinancialForm] = useState({
    startup_id: '', startup_name: '', period: '', revenue: '', expenses: '',
    mrr: '', burn_rate: '', runway_months: '', headcount: '', cash_on_hand: ''
  });
  const [investmentForm, setInvestmentForm] = useState({
    startup_id: '', startup_name: '', amount: '', investment_type: 'equity',
    equity_percentage: '', investment_date: '', valuation_at_investment: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [dashboardRes, revenueRes, investmentsRes, startupsRes] = await Promise.all([
        axios.get(`${API}/financials/dashboard`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/financials/platform-revenue`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/investments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/startups`).catch(() => ({ data: [] }))
      ]);
      
      setDashboardData(dashboardRes.data);
      setPlatformRevenue(revenueRes.data);
      setInvestments(investmentsRes.data || []);
      setStartups(startupsRes.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching financial data:", error);
      setLoading(false);
    }
  };

  const handleAddFinancials = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/financials/startup`, {
        ...financialForm,
        revenue: parseFloat(financialForm.revenue) || 0,
        expenses: parseFloat(financialForm.expenses) || 0,
        mrr: parseFloat(financialForm.mrr) || 0,
        burn_rate: parseFloat(financialForm.burn_rate) || 0,
        runway_months: parseFloat(financialForm.runway_months) || 0,
        headcount: parseInt(financialForm.headcount) || 0,
        cash_on_hand: parseFloat(financialForm.cash_on_hand) || 0
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast({ title: "Success", description: "Financial data added" });
      setAddFinancialsOpen(false);
      setFinancialForm({ startup_id: '', startup_name: '', period: '', revenue: '', expenses: '', mrr: '', burn_rate: '', runway_months: '', headcount: '', cash_on_hand: '' });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to add", variant: "destructive" });
    }
  };

  const handleAddInvestment = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/investments`, {
        ...investmentForm,
        amount: parseFloat(investmentForm.amount) || 0,
        equity_percentage: parseFloat(investmentForm.equity_percentage) || null,
        valuation_at_investment: parseFloat(investmentForm.valuation_at_investment) || null
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast({ title: "Success", description: "Investment recorded" });
      setAddInvestmentOpen(false);
      setInvestmentForm({ startup_id: '', startup_name: '', amount: '', investment_type: 'equity', equity_percentage: '', investment_date: '', valuation_at_investment: '' });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to add", variant: "destructive" });
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value?.toFixed(0) || 0}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  const summary = dashboardData?.portfolio_summary || {};
  const revenue = platformRevenue || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header />
      
      {/* Hero */}
      <div className="bg-gradient-to-r from-green-700 via-emerald-700 to-teal-700 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Financial Dashboard</h1>
              <p className="opacity-90">Portfolio performance & financial metrics</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={fetchData} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4 mr-2" />Refresh
              </Button>
              <Button onClick={() => setAddFinancialsOpen(true)} className="bg-white text-green-700 hover:bg-gray-100">
                <Plus className="h-4 w-4 mr-2" />Add Financials
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-green-600 to-emerald-700 border-none text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="h-5 w-5 opacity-80" />
                <ArrowUpRight className="h-4 w-4" />
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.total_invested || 0)}</p>
              <p className="text-xs opacity-80">Total Invested</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 border-none text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="h-5 w-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold">{summary.portfolio_companies || 0}</p>
              <p className="text-xs opacity-80">Portfolio Companies</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-600 to-violet-700 border-none text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="h-5 w-5 opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">+{dashboardData?.trends?.revenue_growth || 0}%</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.portfolio_revenue || 0)}</p>
              <p className="text-xs opacity-80">Portfolio Revenue</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-600 to-amber-700 border-none text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="h-5 w-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.portfolio_mrr || 0)}</p>
              <p className="text-xs opacity-80">Portfolio MRR</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-600 to-teal-700 border-none text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Clock className="h-5 w-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold">{summary.avg_runway_months || 0}mo</p>
              <p className="text-xs opacity-80">Avg Runway</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-pink-600 to-rose-700 border-none text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="h-5 w-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold">{summary.total_headcount || 0}</p>
              <p className="text-xs opacity-80">Total Headcount</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-800/50 border border-gray-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-green-600">Overview</TabsTrigger>
            <TabsTrigger value="investments" className="data-[state=active]:bg-blue-600">Investments</TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-purple-600">Platform Revenue</TabsTrigger>
            <TabsTrigger value="startups" className="data-[state=active]:bg-orange-600">Startup Metrics</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Revenue Chart */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Monthly Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={revenue.monthly_breakdown?.slice().reverse() || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                      <YAxis stroke="#9ca3af" fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none' }} />
                      <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Investment Distribution */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Investment Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={investments.reduce((acc, inv) => {
                          const type = inv.investment_type || 'equity';
                          const existing = acc.find(a => a.name === type);
                          if (existing) existing.value += inv.amount;
                          else acc.push({ name: type, value: inv.amount });
                          return acc;
                        }, [])}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {investments.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Investments Tab */}
          <TabsContent value="investments">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Investment Records</CardTitle>
                  <Button onClick={() => setAddInvestmentOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" />Add Investment
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {investments.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No investments recorded yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Startup</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Amount</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Type</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Equity %</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {investments.map((inv) => (
                          <tr key={inv.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                            <td className="py-3 px-4 text-white font-medium">{inv.startup_name}</td>
                            <td className="py-3 px-4 text-green-400">{formatCurrency(inv.amount)}</td>
                            <td className="py-3 px-4">
                              <Badge className="bg-blue-600">{inv.investment_type}</Badge>
                            </td>
                            <td className="py-3 px-4 text-gray-300">{inv.equity_percentage ? `${inv.equity_percentage}%` : '-'}</td>
                            <td className="py-3 px-4 text-gray-400">{inv.investment_date}</td>
                            <td className="py-3 px-4">
                              <Badge className={inv.status === 'active' ? 'bg-green-600' : inv.status === 'exited' ? 'bg-purple-600' : 'bg-red-600'}>
                                {inv.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Platform Revenue Tab */}
          <TabsContent value="revenue">
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              <Card className="bg-gradient-to-br from-green-900/50 to-emerald-900/50 border-green-700">
                <CardContent className="p-6 text-center">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-green-400" />
                  <p className="text-3xl font-bold text-white">{formatCurrency(revenue.total_revenue || 0)}</p>
                  <p className="text-gray-400">Total Platform Revenue</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-900/50 to-indigo-900/50 border-blue-700">
                <CardContent className="p-6 text-center">
                  <Target className="h-8 w-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-3xl font-bold text-white">{revenue.payment_count || 0}</p>
                  <p className="text-gray-400">Total Subscriptions</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-900/50 to-violet-900/50 border-purple-700">
                <CardContent className="p-6 text-center">
                  <Wallet className="h-8 w-8 mx-auto mb-2 text-purple-400" />
                  <p className="text-3xl font-bold text-white">${revenue.subscription_price || 9}</p>
                  <p className="text-gray-400">Subscription Price</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Monthly Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenue.monthly_breakdown?.slice().reverse() || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none' }} />
                    <Bar dataKey="revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Startup Metrics Tab */}
          <TabsContent value="startups">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Recent Startup Financials</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.recent_financials?.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No startup financials recorded yet. Click "Add Financials" to get started.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Startup</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Period</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Revenue</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">MRR</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Burn Rate</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Runway</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Headcount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData?.recent_financials?.map((fin) => (
                          <tr key={fin.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                            <td className="py-3 px-4 text-white font-medium">{fin.startup_name}</td>
                            <td className="py-3 px-4 text-gray-400">{fin.period}</td>
                            <td className="py-3 px-4 text-green-400">{formatCurrency(fin.revenue)}</td>
                            <td className="py-3 px-4 text-blue-400">{formatCurrency(fin.mrr)}</td>
                            <td className="py-3 px-4 text-orange-400">{formatCurrency(fin.burn_rate)}/mo</td>
                            <td className="py-3 px-4 text-cyan-400">{fin.runway_months}mo</td>
                            <td className="py-3 px-4 text-gray-300">{fin.headcount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Financials Dialog */}
      <Dialog open={addFinancialsOpen} onOpenChange={setAddFinancialsOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Startup Financials</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Startup</label>
              <Select value={financialForm.startup_id} onValueChange={(v) => {
                const startup = startups.find(s => s.id === v);
                setFinancialForm({...financialForm, startup_id: v, startup_name: startup?.name || ''});
              }}>
                <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue placeholder="Select startup" /></SelectTrigger>
                <SelectContent>
                  {startups.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Period (e.g., 2026-01)</label>
              <Input value={financialForm.period} onChange={(e) => setFinancialForm({...financialForm, period: e.target.value})} placeholder="YYYY-MM" className="bg-gray-900 border-gray-600" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Revenue ($)</label>
                <Input type="number" value={financialForm.revenue} onChange={(e) => setFinancialForm({...financialForm, revenue: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">MRR ($)</label>
                <Input type="number" value={financialForm.mrr} onChange={(e) => setFinancialForm({...financialForm, mrr: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Burn Rate ($/mo)</label>
                <Input type="number" value={financialForm.burn_rate} onChange={(e) => setFinancialForm({...financialForm, burn_rate: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Runway (months)</label>
                <Input type="number" value={financialForm.runway_months} onChange={(e) => setFinancialForm({...financialForm, runway_months: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Cash on Hand ($)</label>
                <Input type="number" value={financialForm.cash_on_hand} onChange={(e) => setFinancialForm({...financialForm, cash_on_hand: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Headcount</label>
                <Input type="number" value={financialForm.headcount} onChange={(e) => setFinancialForm({...financialForm, headcount: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddFinancialsOpen(false)} className="border-gray-600">Cancel</Button>
            <Button onClick={handleAddFinancials} className="bg-green-600 hover:bg-green-700">Save Financials</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Investment Dialog */}
      <Dialog open={addInvestmentOpen} onOpenChange={setAddInvestmentOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Investment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Startup</label>
              <Select value={investmentForm.startup_id} onValueChange={(v) => {
                const startup = startups.find(s => s.id === v);
                setInvestmentForm({...investmentForm, startup_id: v, startup_name: startup?.name || ''});
              }}>
                <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue placeholder="Select startup" /></SelectTrigger>
                <SelectContent>
                  {startups.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Amount ($)</label>
                <Input type="number" value={investmentForm.amount} onChange={(e) => setInvestmentForm({...investmentForm, amount: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Type</label>
                <Select value={investmentForm.investment_type} onValueChange={(v) => setInvestmentForm({...investmentForm, investment_type: v})}>
                  <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="convertible_note">Convertible Note</SelectItem>
                    <SelectItem value="safe">SAFE</SelectItem>
                    <SelectItem value="grant">Grant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Equity %</label>
                <Input type="number" value={investmentForm.equity_percentage} onChange={(e) => setInvestmentForm({...investmentForm, equity_percentage: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Date</label>
                <Input type="date" value={investmentForm.investment_date} onChange={(e) => setInvestmentForm({...investmentForm, investment_date: e.target.value})} className="bg-gray-900 border-gray-600" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Valuation at Investment ($)</label>
              <Input type="number" value={investmentForm.valuation_at_investment} onChange={(e) => setInvestmentForm({...investmentForm, valuation_at_investment: e.target.value})} className="bg-gray-900 border-gray-600" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddInvestmentOpen(false)} className="border-gray-600">Cancel</Button>
            <Button onClick={handleAddInvestment} className="bg-blue-600 hover:bg-blue-700">Save Investment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-6 mt-12">
        <div className="container mx-auto px-6 text-center text-gray-400">
          <p>© 2026 SKI AXIS. Financial data is confidential.</p>
        </div>
      </footer>
    </div>
  );
};

export default FinancialDashboard;
